import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AppConfig } from '../../../config/appConfig';
import {
  ContactMessagePayload,
  ContactMessageService,
} from '../../services/contact-message.service';

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.css',
})
export class ContactComponent implements OnDestroy {
  readonly title = AppConfig.title;
  readonly contactData = AppConfig.contactData;

  /** Reactive form backing the contact message UI. */
  readonly contactForm = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(100)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(254)]],
    subject: ['', [Validators.maxLength(150)]],
    message: ['', [Validators.required, Validators.maxLength(2000)]],
    website: [''],
    gdprConsent: [true, [Validators.requiredTrue]],
  });

  /** Flags used to drive feedback messages in the template. */
  isSubmitting = false;
  submissionSuccess = false;
  submissionError = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly contactMessageService: ContactMessageService,
  ) {}

  /** Convenience getter for easier template access to the form controls. */
  get controls() {
    return this.contactForm.controls;
  }

  /** Handles form submission by validating, sending, and surfacing results. */
  submit(): void {
    this.submissionError = '';
    this.submissionSuccess = false;

    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const payload: ContactMessagePayload = this.contactForm.getRawValue() as ContactMessagePayload;

    this.contactMessageService
      .submit(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe({
        next: () => {
          this.submissionSuccess = true;
          this.contactForm.reset({ gdprConsent: true });
        },
        error: (error) => {
          console.error('Failed to send contact message', error);
          this.submissionError =
            'Something went wrong while sending your message. Please try again or email us directly.';
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
